import styled from "@emotion/styled";
import { Pie, PieChart } from "recharts";

import { ChartContainer, ChartTooltip, ChartTooltipContent } from "components/ui/chart/Chart";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { FeatureUsage } from "lib/hooks/api/usage/useCompanyFeatureUsage";
import FeatureUsageBars from "./FeatureUsageBars";
import { FEATURE_USAGE_CONFIG } from "./featureUsageConfig";
import FeatureUsageLegend from "./FeatureUsageLegend";

export type FeatureUsageChartType = "pie" | "bar";

type Props = {
  /** Every feature, most used first. */
  features: FeatureUsage[];
  chartType: FeatureUsageChartType;
  isLoading?: boolean;
};

/** Roughly a row per feature, so finishing loading barely moves the page. */
const LOADING_HEIGHT = 320;

/** Each feature's days used: a pie with a legend, or a bar per feature. */
function FeatureUsageChart({ features, chartType, isLoading }: Props) {
  if (isLoading) {
    return (
      <ChartFrame>
        <CentredSpinner label="Loading feature usage chart" />
      </ChartFrame>
    );
  }

  if (chartType === "bar") {
    return <FeatureUsageBars features={features} />;
  }

  return <FeatureUsagePie features={features} />;
}

/** Each feature's share of the days used, keyed by colour to a legend of the figures. */
function FeatureUsagePie({ features }: { features: FeatureUsage[] }) {
  const slices = features
    .filter((feature) => feature.daysUsed > 0)
    .map((feature) => ({ ...feature, fill: `var(--color-${feature.entityType})` }));

  return (
    <PieBreakdown>
      <PieContainer config={FEATURE_USAGE_CONFIG} aspectRatio="1 / 1">
        <PieChart accessibilityLayer>
          <ChartTooltip content={<ChartTooltipContent hideLabel />} />
          {/* Drawn at once, so switching back from the bars doesn't replay a sweep. */}
          <Pie
            data={slices}
            dataKey="daysUsed"
            nameKey="entityType"
            outerRadius="100%"
            isAnimationActive={false}
          />
        </PieChart>
      </PieContainer>
      <FeatureUsageLegend features={features} />
    </PieBreakdown>
  );
}

const ChartFrame = styled.div({
  height: LOADING_HEIGHT,
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const PieBreakdown = styled.div({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 48,
});

const PieContainer = styled(ChartContainer)({
  width: 220,
  flexShrink: 0,
});

export default FeatureUsageChart;
